import { useState } from "react";
import { SummaryMetricType } from "../../SummaryMetricsRow";
import { FlightDashboardButton } from "../../Buttons/FlightButtons";
import { PnlTimes } from "../PnLChart/pnlChartTypes";
import BreakdownRow from "./BreakdownRow";
import { PnlBreakdown } from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import {
  MetricAndPnLBreakdownStyles,
  SkuBreakdownStyles,
} from "./metricAndPnLBreakdownStyles";
import { Collapse, useMediaQuery } from "@mui/material";
import { BREAKPOINT_MOBILE } from "../../../../constants/constants";
import ProcessPnLBreakdown from "./ProcessPnLBreakdown";
import { getPnlTimes } from "../PnLChart/pnlChartUtils";
import {
  getSummaryMetrics,
  sortByOrder,
  SummaryMetricInput,
  useSummaryMetrics,
} from "reports/utils/metricUtils";
import ReportButton from "reports/components/Reports/Buttons/ReportButton";
import { useTranslation } from "react-i18next";
import { useTargetsFromCustomerJson } from "reports/utils/customerInfoUtils";
import { BreakdownTypeEnum, BreakdownType } from "./metricAndPnlBreakdownTypes";

export default function MetricAndPnLBreakdown({
  pnlBreakdown,
  processName,
  processId,
  skuData,
  pnlTimes,
  maxBreakdownHeaderLength,
  summaryMetricsInputList,
  getCustomSummaryMetricsInputListWithPnlBreakdown,
  showMetricTitle = false,
  flightDashboardLink,
  customSkusTitle,
  reportLink,
  breakdownType = BreakdownTypeEnum.process,
  shouldExpand = true,
  shouldShowButterfly = true,
  shouldEnableToggle = true,
}: {
  pnlBreakdown: PnlBreakdown;
  processName: string;
  processId: string;
  pnlTimes: PnlTimes[];
  skuData: PnlBreakdown[];
  maxBreakdownHeaderLength: number;
  showMetricTitle?: boolean;
  summaryMetricsInputList: SummaryMetricInput[];
  getCustomSummaryMetricsInputListWithPnlBreakdown?: (
    pnlBreakdown: PnlBreakdown
  ) => SummaryMetricInput[];
  flightDashboardLink?: string | null;
  customSkusTitle?: string;
  reportLink?: string;
  breakdownType?: BreakdownType;
  shouldExpand?: boolean;
  shouldShowButterfly?: boolean;
  /** if false, MetricAndPnlBreakdown start in expanded state and cannot collapse */
  shouldEnableToggle?: boolean;
}) {
  const { t } = useTranslation("EOSReport");
  const [isExpanded, setIsExpanded] = useState(!shouldEnableToggle);
  const toggleExpand = () => {
    shouldExpand && setIsExpanded(!isExpanded);
  };
  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );
  const targets = useTargetsFromCustomerJson();
  const useShortName = true; // always use short name in MachineBreakdown

  const getCustomAndDefaultSummaryMetricsInputList = (
    pnlBreakdown: PnlBreakdown
  ) =>
    summaryMetricsInputList.concat(
      getCustomSummaryMetricsInputListWithPnlBreakdown
        ? getCustomSummaryMetricsInputListWithPnlBreakdown(pnlBreakdown)
        : []
    );

  const summaryMetrics = useSummaryMetrics({
    metrics: getCustomAndDefaultSummaryMetricsInputList(pnlBreakdown),
    pnlBreakdown,
    useShortName,
  }).sort(sortByOrder);

  const skuDataFiltered = skuData?.filter(({ sku }) => sku !== null);
  const nonNullSkuBreakdownRowData = skuDataFiltered.map(skuDatum => {
    const metrics =
      getCustomAndDefaultSummaryMetricsInputList(skuDatum).sort(sortByOrder);
    const skuSummaryMetrics = getSummaryMetrics({
      t,
      targets,
      metrics,
      pnlBreakdown: skuDatum,
      useShortName,
    });
    const skuPnlBreakdown = getPnlTimes({ data: skuDatum });
    const range = {
      ge: skuDatum.ge,
      lt: skuDatum.lt,
    };
    return {
      sku: skuDatum.sku,
      skuDescription: skuDatum.skuDescription,
      skuSummaryMetrics,
      skuPnlBreakdown,
      range,
    } as {
      sku: string;
      skuDescription?: string;
      skuSummaryMetrics: SummaryMetricType[];
      skuPnlBreakdown: PnlTimes[];
      range: {
        ge: number;
        lt: number;
      };
    };
  });

  const numberOfSkus = nonNullSkuBreakdownRowData?.length ?? 0;
  const skusTitle =
    numberOfSkus <= 0
      ? ""
      : numberOfSkus === 1
        ? nonNullSkuBreakdownRowData[0].sku || ""
        : `${numberOfSkus} ${customSkusTitle ? customSkusTitle : "SKUs"}`;

  /** report link is redundant if we're already on this page */
  const isReportLinkRedundant = reportLink?.includes(window.location.pathname);

  return (
    <>
      <MetricAndPnLBreakdownStyles
        {...{
          $isExpanded: isExpanded,
          $shouldShowButterfly: shouldShowButterfly,
          $shouldExpand: shouldExpand,
        }}
        onClick={shouldEnableToggle ? toggleExpand : undefined}
      >
        <BreakdownRow
          breakdownName={processName}
          breakdownRange={{
            ge: pnlBreakdown.ge,
            lt: pnlBreakdown.lt,
          }}
          breakdownSummaryMetrics={summaryMetrics.sort(sortByOrder)}
          breakdownPnlTimes={pnlTimes}
          {...{
            showMetricTitle,
            skusTitle,
            maxBreakdownHeaderLength,
            isExpanded,
            shouldExpand,
            shouldShowButterfly,
            reportLink,
            isFirstRow: true,
            isLastRow: true,
            processId,
          }}
        />
        <Collapse in={isExpanded}>
          <SkuBreakdownStyles
            style={{ borderRadius: numberOfSkus > 1 ? 0 : "0 0 10px 10px" }}
          >
            <ProcessPnLBreakdown
              pnlTimes={pnlTimes}
              breakdownType={breakdownType}
            />
            <>
              {numberOfSkus > 0 &&
                nonNullSkuBreakdownRowData?.map(
                  (
                    {
                      sku,
                      skuDescription,
                      skuSummaryMetrics,
                      skuPnlBreakdown,
                      range,
                    },
                    idx
                  ) => (
                    <BreakdownRow
                      key={sku}
                      breakdownRange={range}
                      showMetricTitle={isMobileOrSmaller}
                      breakdownName={sku}
                      breakdownSummaryMetrics={skuSummaryMetrics}
                      breakdownPnlTimes={skuPnlBreakdown}
                      isSkuBreakdown={true}
                      maxBreakdownHeaderLength={maxBreakdownHeaderLength}
                      isFirstRow={idx === 0}
                      isLastRow={idx === numberOfSkus - 1}
                      processId={processId}
                      skusTitle={skuDescription}
                    />
                  )
                )}
            </>
          </SkuBreakdownStyles>
          {flightDashboardLink || reportLink ? (
            <div className="bottomButtonsRow">
              {flightDashboardLink && (
                <FlightDashboardButton href={flightDashboardLink} />
              )}
              {reportLink && !isReportLinkRedundant && (
                <ReportButton href={reportLink} />
              )}
            </div>
          ) : null}
        </Collapse>
      </MetricAndPnLBreakdownStyles>
    </>
  );
}
